/**
 * Pure helpers for the OFFLINE path-traced render pipeline (pt-capture).
 *
 * Everything in this file is side-effect free so it can be unit tested without a
 * WebGL context.  None of it touches the realtime viewport — it only feeds the
 * cloned path-tracer scene.
 */
import { cameraFramePadding } from './geometry-helpers';
import type {
  CameraPose as SceneCameraPose,
  FacadeFacing as SceneFacadeFacing,
  MaterialProfile,
  ProductCameraPreset as SceneViewId,
  Vec3 as SceneVec3,
} from '../types';

export type PtQuality = 'standard' | 'final_photo' | 'client_photo';

/**
 * Target sample counts per "sample level".  Levels 0-5 are the historical
 * quick-preview / catalog tiers.  6-8 are the new client-photo tiers that trade
 * time for noise-free convergence.
 */
export const TARGET_BY_LEVEL: Record<number, number> = {
  0: 8,
  1: 12,
  2: 18,
  3: 32,
  4: 56,
  5: 96,
  6: 192,
  7: 384,
  8: 640,
};

export function sampleLevelToTarget(level: number): number {
  return TARGET_BY_LEVEL[level] ?? 28;
}

/** Ray bounces per quality tier.  Client-photo pushes to 12 for richer GI. */
export function bouncesForQuality(quality: PtQuality): number {
  if (quality === 'client_photo') return 12;
  if (quality === 'final_photo') return 8;
  return 5;
}

export type ResolutionClampInput = {
  /** Base (viewport) pixel dimensions the scale multiplies. */
  baseWidth: number;
  baseHeight: number;
  /** Requested super-sampling factor (e.g. 2.5 for client photo). */
  requestedScale: number;
  /** Hard cap on the longest output side.  Defaults to 3840 (4K-ish). */
  maxOutputPx?: number;
  /** GPU max texture size, read from renderer.capabilities.maxTextureSize. */
  maxTextureSize?: number;
  /** Lower bound so we never render below the viewport quality floor. */
  minScale?: number;
  /** Absolute upper bound on the multiplier regardless of pixel budget. */
  maxScale?: number;
};

/**
 * Clamp the requested super-sampling scale so the output never exceeds the GPU
 * texture budget (avoids allocation failures / context loss on huge renders).
 *
 * The longest output side is held at or below `min(maxOutputPx, maxTextureSize)`.
 */
export function clampResolutionScale(input: ResolutionClampInput): number {
  const {
    baseWidth,
    baseHeight,
    requestedScale,
    maxOutputPx = 3840,
    maxTextureSize = 8192,
    minScale = 0.75,
    maxScale = 3,
  } = input;
  const longestBase = Math.max(1, baseWidth, baseHeight);
  const pixelBudget = Math.max(1, Math.min(maxOutputPx, maxTextureSize));
  const budgetScale = pixelBudget / longestBase;
  const capped = Math.min(requestedScale, maxScale, budgetScale);
  return Math.max(minScale, capped);
}

// ---------------------------------------------------------------------------
// Multi-view "Снять комплект" camera presets
// ---------------------------------------------------------------------------

/**
 * Ракурсы, поза камеры и сторона фасада живут в `../types` — здесь они только
 * переэкспортированы под привычными именами.  До этого файл объявлял свои копии
 * `Vec3`, `CameraPose`, `FacadeFacing` и список ракурсов; копии совпадали с
 * `../types` буква в букву, но ничем не были связаны, а встречались на живом
 * вызове `computeViewPose(request.cameraPreset, …)` в `SceneCanvas`.
 *
 * `ViewId` — прежнее имя для `ProductCameraPreset`; список ракурсов теперь
 * ровно один.
 */
export type { Vec3, CameraPose, FacadeFacing } from '../types';
export type { ProductCameraPreset as ViewId } from '../types';

export type ViewBounds = {
  center: SceneVec3;
  size: SceneVec3;
};

export const PHOTO_VIEW_SET: ReadonlyArray<{ id: SceneViewId; label: string }> = [
  { id: 'hero', label: 'Ракурс' },
  { id: 'front', label: 'Фронт' },
  { id: 'technical_oblique', label: 'Технический 3/4' },
  { id: 'three_quarter_left', label: '3/4 слева' },
  { id: 'side', label: 'Сбоку' },
  { id: 'top', label: 'Сверху' },
];

/**
 * Compute a deterministic camera pose for a standard product view from the
 * scene bounds.  Mirrors the framing logic of `applyProjection` (useThreeScene)
 * so batch renders match the live projections, but returns plain values instead
 * of mutating a live camera.
 */
export function computeViewPose(
  id: SceneViewId,
  bounds: ViewBounds,
  fovDeg = 45,
  facing: SceneFacadeFacing = '+z',
): SceneCameraPose {
  const [cx, cy, cz] = bounds.center;
  const [sx, sy, sz] = bounds.size;
  const maxDim = Math.max(sx, sy, sz, 1);
  const target: SceneVec3 = [cx, cy, cz];
  const up: SceneVec3 = [0, 1, 0];
  // `facing` is a graph fact, not a camera guess.  The recognizer may place a
  // return leg on +/-X; using a hard-coded +Z camera then photographs its side
  // or back and makes a correct module look wrong in the comparison report.
  const horizontalBasis: Record<SceneFacadeFacing, { front: [number, number]; right: [number, number] }> = {
    '+z': { front: [0, 1], right: [1, 0] },
    '-z': { front: [0, -1], right: [-1, 0] },
    '+x': { front: [1, 0], right: [0, -1] },
    '-x': { front: [-1, 0], right: [0, 1] },
  };
  const basis = horizontalBasis[facing];
  const offset = (front: number, right = 0): [number, number] => [
    basis.front[0] * front + basis.right[0] * right,
    basis.front[1] * front + basis.right[1] * right,
  ];

  switch (id) {
    case 'hero': {
      const fov = (fovDeg * Math.PI) / 180;
      // A product render is not a showroom navigation view.  The old pose
      // reused the roomy viewport padding, which left furniture tiny whenever
      // a floor or room context was present.  Keep a safe 8% margin around the
      // furniture envelope while making the default evidence/photo readable.
      const persDist = (maxDim / 2 / Math.tan(fov / 2)) * Math.min(cameraFramePadding(maxDim), 1.08);
      const [ox, oz] = offset(persDist * 0.95, persDist * 0.82);
      return {
        position: [cx + ox, cy + persDist * 0.58, cz + oz],
        target,
        up,
        fovDeg,
      };
    }
    case 'front':
      { const [ox, oz] = offset(maxDim * 1.45);
      return {
        position: [cx + ox, cy + sy * 0.35, cz + oz],
        target,
        up,
        fovDeg,
      }; }
    case 'technical_oblique': {
      // The drawing-like comparison pose intentionally exposes both arms
      // while staying close to an elevation: it is deterministic evidence,
      // not the arbitrary showroom orbit camera.
      const [ox, oz] = offset(maxDim * 1.3, -maxDim * 0.72);
      return {
        position: [cx + ox, cy + sy * 0.42, cz + oz],
        target,
        up,
        fovDeg,
      };
    }
    case 'three_quarter_left':
      { const [ox, oz] = offset(maxDim * 1.05, -maxDim * 1.05);
      return {
        position: [cx + ox, cy + maxDim * 0.78, cz + oz],
        target,
        up,
        fovDeg,
      }; }
    case 'side':
      { const [ox, oz] = offset(0, maxDim * 1.45);
      return {
        position: [cx + ox, cy + sy * 0.35, cz + oz],
        target,
        up,
        fovDeg,
      }; }
    case 'top':
      return {
        position: [cx, cy + maxDim * 2.2, cz + 0.001],
        target,
        // looking straight down: keep a stable screen-up along -Z
        up: [0, 0, -1],
        fovDeg,
      };
    default:
      return { position: [cx, cy, cz + maxDim * 2.2], target, up, fovDeg };
  }
}

// ---------------------------------------------------------------------------
// Role → physically-correct PBR params (PT clone only)
// ---------------------------------------------------------------------------

export type PbrRole = 'metal' | 'glass' | 'panel';

export type MaterialRoleInput = {
  /** Material profile from the render contract, if known. */
  profile?: MaterialProfile | string | null;
  /** Live-material metalness (falls back to profile inference). */
  metalness?: number;
  /** Whether the live material is transparent / has transmission. */
  transparent?: boolean;
  transmission?: number;
  opacity?: number;
  /** Free-text material name (used for a coarse metal/glass hint). */
  name?: string;
};

/** Classify a cloned material into a physically-meaningful render role. */
export function detectMaterialRole(input: MaterialRoleInput): PbrRole {
  const profile = String(input.profile ?? '').toLowerCase();
  const name = String(input.name ?? '').toLowerCase();

  const glassByProfile = profile === 'glass';
  const glassByProps =
    (input.transmission ?? 0) > 0.1 ||
    (input.transparent === true && (input.opacity ?? 1) < 0.9);
  if (glassByProfile || glassByProps) return 'glass';

  const metalByProfile = profile === 'metal' || profile === 'mirror';
  const metalByProps = (input.metalness ?? 0) >= 0.4;
  // No \b word boundaries: JS \b keys off ASCII \w, so it misses Cyrillic terms.
  const metalByName = /(chrome|steel|inox|metal|хром|сталь|металл|нерж)/.test(name);
  if (metalByProfile || metalByProps || metalByName) return 'metal';

  return 'panel';
}

export type PbrParams = {
  metalness: number;
  roughnessMin: number;
  transmission: number;
  ior: number;
  thinFilm: boolean;
};

/** Physically-correct PhysicalPathTracingMaterial params per role. */
export function pbrParamsForRole(role: PbrRole): PbrParams {
  switch (role) {
    case 'metal':
      return { metalness: 1, roughnessMin: 0.08, transmission: 0, ior: 1.45, thinFilm: false };
    case 'glass':
      return { metalness: 0, roughnessMin: 0, transmission: 1, ior: 1.5, thinFilm: true };
    case 'panel':
    default:
      return { metalness: 0, roughnessMin: 0.35, transmission: 0, ior: 1.45, thinFilm: false };
  }
}

// ---------------------------------------------------------------------------
// Real Egger decor scans (public/textures) → decor-code registry
// ---------------------------------------------------------------------------

/**
 * The decor scans that physically ship in public/textures/.  Keyed by the Egger
 * decor code embedded in the file name; the same codes appear in the material
 * visual profile labels and therefore in the render contract's
 * material `name`.  This is a real, data-driven mapping — NOT a synthetic
 * fallback.  A panel only receives a scan when its material name confidently
 * contains one of these codes; otherwise it keeps its existing surface.
 */
export const DECOR_TEXTURE_BY_CODE: Readonly<Record<string, string>> = {
  H1180: '/textures/egger_h1180_halifax_oak.jpeg',
  H1277: '/textures/egger_h1277_st9.jpeg',
  // Этот скан ЛЕЖАЛ в сборке, но в таблице его не было: файл возили, а
  // сопоставить его артикулу было нечем.  Привязка не угадана — её уже держит
  // `pro100-layered-review-projection.ts`
  // (`FACADE_CASELLA_LIGHT_DECOR_TEXTURE_URL`); здесь она просто записана там,
  // где её ищут остальные.
  H1367: '/textures/egger_h1367_light_natural_casella_oak.png',
  H3303: '/textures/egger_h3303_hamilton_oak.jpg',
  U999: '/textures/egger_u999_black.jpg',
  W1100: '/textures/egger_w1100_alpine_white.jpg',
};

/**
 * Resolve a real decor scan URL from a material name by extracting an Egger
 * decor code (e.g. "EGGER H1180 ST37 Дуб Halifax" → H1180 texture).  Returns
 * null when no shipped scan matches so callers can fall back to the existing
 * (synthetic) surface instead of faking a decor.
 */
export function resolveDecorTextureUrl(materialName: string | null | undefined): string | null {
  const text = String(materialName ?? '').toUpperCase();
  const match = text.match(/\b([HWUF]\d{3,4})\b/);
  if (!match) return null;
  return DECOR_TEXTURE_BY_CODE[match[1]] ?? null;
}
